import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import './PreviewSurvey.css';
import './PreviewSurveyDateTime.css';
import QuestionRenderer from './QuestionRenderer';
import {
  Container, Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  IconButton,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  MobileStepper,
  useTheme,
  useMediaQuery,
  Rating,
  Input,
  Checkbox,
  Alert,
  OutlinedInput,
  FormHelperText,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Slider,
  Grid,
  Select,
  MenuItem,

} from '@mui/material';
import { alpha } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ImageIcon from '@mui/icons-material/Image';
import { getRankingItems, reorderRankingItems } from '../../utils/ranking';
import LogicEvaluationService from '../../services/LogicEvaluationService';
import API_CONFIG, { getApiUrl } from '../../config/api';
import {
  Close as CloseIcon,
  KeyboardArrowLeft as PrevIcon,
  KeyboardArrowRight as NextIcon,
  Upload as UploadIcon,
  Phone as PhoneIcon,
  Numbers as NumbersIcon,
  DragIndicator,
  Language as WebsiteIcon,
  Home as HomeIcon,
  LocationCity as LocationCityIcon,
  Terrain as TerrainIcon,
  MarkunreadMailbox as MarkunreadMailboxIcon,
  Public as PublicIcon,

} from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
const questionTypes = {
  'likert-scale': {
    config: {
      scale: [
        'Strongly Disagree',
        'Disagree',
        'Neutral',
        'Agree',
        'Strongly Agree'
      ]
    }
  }
};

const formatDateToUserFormat = (dateStr, question) => {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  const format = question?.dateFormat || 'YYYY-MM-DD';
  console.log(dateStr, question);
  console.log(format);

  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM-DD-YYYY':
      return `${month}-${day}-${year}`;
    default: // YYYY-MM-DD
      return `${year}-${month}-${day}`;
  }
};

const PreviewSurvey = ({ open, onClose, survey, questions }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [rankingItems, setRankingItems] = useState({});
  const [logicRules, setLogicRules] = useState([]);
  const [logicError, setLogicError] = useState(null);
  const [logicLoading, setLogicLoading] = useState(false);
  const [previewEnded, setPreviewEnded] = useState(false);
  const logicEvaluationService = new LogicEvaluationService();
  const logicEvaluation = logicEvaluationService.evaluateRules(logicRules, answers, questions);
  const questionVisibility = logicEvaluationService.getQuestionVisibility(questions, logicEvaluation);
  const visibleQuestionIndexes = questions
    .map((question, index) => questionVisibility[String(question.id)] !== false ? index : -1)
    .filter(index => index >= 0);
  const currentVisiblePosition = visibleQuestionIndexes.indexOf(activeStep);
  const lastVisibleQuestionIndex = visibleQuestionIndexes[visibleQuestionIndexes.length - 1];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isClassicLayout = survey?.layout === 'classic';
  const dialogContentStyle = {
    pt: isMobile ? 8 : 20,
    pb: 4,
    px: 0,
    bgcolor: 'background.default',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative'
  };

  const navigationStyle = {
    position: 'fixed',
    bottom: 40,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    px: 4,
    maxWidth: '1400px',
    mx: 'auto'
  };

  const questionContainerStyle = {
    width: '100%',
    maxWidth: '100%',
    px: { xs: 2, sm: 4 },
    mx: 'auto',
    position: 'relative',
    '& .MuiFormControl-root': {
      width: '100%'
    }
  };

  useEffect(() => {
    console.log('useEffect triggered with questions:', questions);
    // Initialize ranking items when questions change
    const newRankingItems = {};
    questions.forEach((question, index) => {
      if (question.type === 'ranking') {
        // Use the same key format as in renderQuestion
        const key = question.id || `temp-question-${index}`;
        
        console.log('Processing ranking question:', question);

        const items = getRankingItems(question);
        if (items.length > 0) {
          newRankingItems[key] = items;
        }
      }
    });
    if (Object.keys(newRankingItems).length > 0) {
      setRankingItems(newRankingItems);
    }
  }, [questions]);

  const getThemeConfig = () => {
    if (!survey?.theme) return theme;

    // Check if this is a dark theme (dark-modern or discord-dark)
    const isDarkTheme = survey.theme.id === 'dark-modern' || survey.theme.id === 'discord-dark';
    
    return createTheme({
      palette: {
        primary: { main: survey.theme.accentColor || '#358c7c' },
        background: {
          default: survey.theme.bg || '#ffffff',
          paper: survey.theme.bg || '#ffffff'
        },
        text: {
          primary: survey.theme.textColor || '#111827',
          // For dark themes, ensure secondary text is visible
          secondary: isDarkTheme ? '#ffffff' : 'text.secondary'
        }
      }
    });
  };

  const customTheme = getThemeConfig();

  useEffect(() => {
    if (!open || !survey?.id) return;
    let cancelled = false;
    const loadLogicRules = async () => {
      setLogicLoading(true);
      try {
        setLogicError(null);
        const response = await fetch(
          `${getApiUrl(API_CONFIG.ENDPOINTS.LOGIC_RULES.LIST)}?survey_id=${encodeURIComponent(survey.id)}`
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Unable to load survey logic rules');
        }
        if (!cancelled) {
          setLogicRules((data.data?.rules || []).map(rule => ({
            ...rule,
            enabled: rule.enabled === true || rule.enabled === 1 || rule.enabled === '1'
          })));
        }
      } catch (error) {
        if (!cancelled) setLogicError(error.message);
      } finally {
        if (!cancelled) setLogicLoading(false);
      }
    };
    loadLogicRules();
    return () => { cancelled = true; };
  }, [open, survey?.id]);

  useEffect(() => {
    if (visibleQuestionIndexes.length === 0) return;
    if (!visibleQuestionIndexes.includes(activeStep)) {
      const nextIndex = visibleQuestionIndexes.find(index => index > activeStep);
      setActiveStep(nextIndex === undefined ? visibleQuestionIndexes[visibleQuestionIndexes.length - 1] : nextIndex);
    }
  }, [activeStep, visibleQuestionIndexes.join(',')]);


  const handleAnswer = (questionId, value) => {
    const currentQuestion = questions.find(question => question.id === questionId) || questions[activeStep];
    let hasError = false;
    let errorMessage = '';

    // Check validation based on question type
    switch (currentQuestion.type) {
      case 'text':
      case 'short-answer':
      case 'long-answer':
        if ((currentQuestion.validation?.minLength || currentQuestion.minLength) &&
          (currentQuestion.validation?.maxLength || currentQuestion.maxLength)) {
          const minLength = currentQuestion.validation?.minLength || currentQuestion.minLength;
          const maxLength = currentQuestion.validation?.maxLength || currentQuestion.maxLength;
          hasError = value.length < minLength || value.length > maxLength;
          errorMessage = currentQuestion.validation?.errorMessage;
        }
        break;

      case 'multiple-choice':
        if (currentQuestion.required && !value) {
          hasError = true;
          errorMessage = currentQuestion.validation?.errorMessage || currentQuestion.errorMessage;
        }
        break;

      case 'checkboxes':
        const selectedOptions = value ? JSON.parse(value) : [];
        if (currentQuestion.required && selectedOptions.length === 0) {
          hasError = true;
          errorMessage = currentQuestion.validation?.errorMessage;
        }
        break;
      case 'dropdown':
        if (currentQuestion.required && !value) {
          hasError = true;
          errorMessage = currentQuestion.errorMessage || 'Please select an option';
        }
        break;
    }

    // Update validation errors with user-defined message
    if (hasError) {
      setValidationErrors(prev => ({
        ...prev,
        [questionId]: errorMessage || currentQuestion.errorMessage
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        [questionId]: null
      }));
    }

    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };





  const handleMatrixAnswer = (questionId, rowIndex, columnIndex) => {
    const currentQuestion = questions.find(question => question.id === questionId) || questions[activeStep];
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || {};

      if (currentQuestion.allowMultipleSelection) {
        // Handle multiple selections
        const currentRowSelections = currentAnswers[rowIndex] || [];
        const newRowSelections = currentRowSelections.includes(columnIndex)
          ? currentRowSelections.filter(col => col !== columnIndex)
          : [...currentRowSelections, columnIndex];

        return {
          ...prev,
          [questionId]: {
            ...currentAnswers,
            [rowIndex]: newRowSelections
          }
        };
      } else {
        // Single selection behavior
        return {
          ...prev,
          [questionId]: {
            ...currentAnswers,
            [rowIndex]: columnIndex
          }
        };
      }
    });

    // Clear validation error when user makes a selection
    if (validationErrors[questionId]) {
      setValidationErrors(prev => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          [rowIndex]: false
        }
      }));
    }
  };

  const validateMatrixSelections = (questionId, rowCount) => {
    const matrixAnswers = answers[questionId] || {};
    const newErrors = {};
    let hasError = false;

    // Check if each row has at least one selection
    for (let i = 0; i < rowCount; i++) {
      const rowAnswer = matrixAnswers[i];
      if (!rowAnswer || (Array.isArray(rowAnswer) && rowAnswer.length === 0)) {
        newErrors[i] = true;
        hasError = true;
      }
    }

    setValidationErrors(prev => ({
      ...prev,
      [questionId]: newErrors
    }));

    return !hasError;
  };

  // const handleNext = () => {
  //   const currentQuestion = questions[activeStep];
  //   if (currentQuestion.required && !answers[currentQuestion.id]) {
  //     setValidationErrors(prev => ({
  //         ...prev,
  //         [currentQuestion.id]: currentQuestion.errorMessage || 'This field is required'
  //     }));
  //     return;
  // }

  //   if (currentQuestion.type === 'matrix' && currentQuestion.required) {
  //     const isValid = validateMatrixSelections(currentQuestion.id, currentQuestion.rows.length);
  //     if (!isValid) {
  //       return;
  //     }
  //   }
  //    // Clear validation errors and proceed
  //    setValidationErrors({});
  //    setActiveStep((prevStep) => Math.min(prevStep + 1, questions.length - 1));

  // };
  const handleNext = () => {
    const currentQuestion = questions[activeStep];
    if (!currentQuestion) return;
    let hasError = false;

    // Check for required fields and validation
    if (currentQuestion.required || currentQuestion.validation) {
      switch (currentQuestion.type) {
        case 'multiple-choice':
          hasError = !answers[currentQuestion.id];
          break;

        case 'checkboxes':
          const selectedOptions = answers[currentQuestion.id] ?
            JSON.parse(answers[currentQuestion.id]) : [];
          hasError = selectedOptions.length === 0;
          break;

        case 'text':
        case 'short-answer':
        case 'long-answer':
        case 'number':
        case 'phone':

          const value = answers[currentQuestion.id] || '';
          const minLength = currentQuestion.validation?.minLength;
          const maxLength = currentQuestion.validation?.maxLength;

          if (minLength && maxLength) {
            hasError = value.length < minLength || value.length > maxLength;
          } else if (currentQuestion.required && !value) {
            hasError = true;
          }
          break;

        default:
          hasError = currentQuestion.required && !answers[currentQuestion.id];
      }

      if (hasError) {
        setValidationErrors(prev => ({
          ...prev,
          [currentQuestion.id]: currentQuestion.validation?.errorMessage || currentQuestion.errorMessage
        }));
        return; // Exit early if there's an error
      }
    }

    // Check matrix validation
    if (currentQuestion.type === 'matrix' && currentQuestion.required) {
      const isValid = validateMatrixSelections(currentQuestion.id, currentQuestion.rows.length);
      if (!isValid) {
        return; // Exit early if matrix validation fails
      }
    }

    if (!hasError) {
      setValidationErrors({});
      const evaluation = logicEvaluationService.evaluateRules(logicRules, answers, questions);
      if (evaluation.endSurvey) {
        setPreviewEnded(true);
        return;
      }

      const jumpTarget = logicEvaluationService.getJumpTarget(evaluation);
      if (jumpTarget && questionVisibility[String(jumpTarget)] !== false) {
        const targetIndex = questions.findIndex(question => String(question.id) === String(jumpTarget));
        if (targetIndex >= 0) {
          setActiveStep(targetIndex);
          return;
        }
      }

      const nextIndex = visibleQuestionIndexes.find(index => index > activeStep);
      if (nextIndex === undefined) setPreviewEnded(true);
      else setActiveStep(nextIndex);
    }
  };
  const handleBack = () => {
    const previousIndex = [...visibleQuestionIndexes].reverse().find(index => index < activeStep);
    if (previousIndex !== undefined) setActiveStep(previousIndex);
  };

  const handleRankingDragStart = (e, questionId, item) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(item.id));
    e.dataTransfer.setData('application/x-surveyslack-ranking-question', String(questionId));
    e.currentTarget.classList.add('dragging');
  };

  const handleRankingDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
  };

  const handleRankingDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleRankingDrop = (e, questionId, targetItem) => {
    e.preventDefault();
    const sourceQuestionId = e.dataTransfer.getData('application/x-surveyslack-ranking-question');
    const draggedId = Number(e.dataTransfer.getData('text/plain'));
    if (sourceQuestionId !== String(questionId) || !Number.isInteger(draggedId)) return;

    const currentItems = rankingItems[questionId] || [];
    const reorderedItems = reorderRankingItems(currentItems, draggedId, targetItem.id);
    if (reorderedItems === currentItems) return;

    setRankingItems((prev) => ({
      ...prev,
      [questionId]: reorderedItems
    }));
    handleAnswer(questionId, JSON.stringify(reorderedItems.map((item) => item.text)));
  };

  const getQuestionType = (type) => {
    const typeMapping = {
      'text': 'short-answer',
      'textarea': 'long-answer',
      'select': 'multiple-choice',
      'radio': 'multiple-choice',
      'checkbox': 'checkboxes',
      'matrix': 'matrix',
      'likert-scale': 'likert-scale',
      'nps': 'nps',
      'ranking': 'ranking',
      'datetime': 'datetime',
      'time': 'time',
      'website': 'website',
      'address': 'address',
    };
    return typeMapping[type] || type;
  };

  const renderQuestion = (question, index) => {
    if (!question) return null;

    const mappedType = getQuestionType(question.type);
    const valueKey = question.id || `temp-question-${index}`;
    // const value = answers[question.id] || '';
    const value = answers[valueKey] || '';
    // Use valueKey for validation/errors so unsaved (no id) questions work correctly
    const hasError = Boolean(validationErrors[valueKey]);
    const errorMessage = validationErrors[valueKey];
    
    // Helper to update answers/validation consistently for this rendered question
    const handleAnswerForThis = (val) => handleAnswer(valueKey, val);

    // Helper function to get option values - handles all question types consistently
    const getOptionValue = (field, defaultValue = '') => {
      // First check if the field exists directly on the question object
      if (question[field] !== undefined) {
        return question[field];
      }

      // Then check in the options object (for most question types)
      if (question.options && typeof question.options === 'object') {
        if (question.options[field] !== undefined) {
          return question.options[field];
        }
      }

      // For matrix questions, also check in the parsed options if it's a string
      if (question.type === 'matrix' && typeof question.options === 'string') {
        try {
          const parsed = JSON.parse(question.options);
          if (parsed[field] !== undefined) {
            return parsed[field];
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }

      return defaultValue;
    };

    switch (mappedType) {
      case 'short-answer':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <TextField
              fullWidth
              placeholder={getOptionValue('placeholder', 'Your answer')}
              value={value || getOptionValue('defaultValue', '')}
              onChange={(e) => handleAnswer(valueKey, e.target.value)}
              size="medium"
              required={Boolean(question.required)}
              key={valueKey}
              error={hasError}
              helperText={hasError ? (getOptionValue('errorMessage') || errorMessage) : ''}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  color: survey?.theme?.textColor || 'text.primary',
                  '& fieldset': {
                    borderColor: survey?.theme?.borderColor || 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: survey?.theme?.accentColor || '#1976d2'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: `${survey?.theme?.accentColor} !important`
                  },
                  '&.Mui-focused': {
                    bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  }
                },
                '& .MuiInputBase-input': {
                  color: survey?.theme?.textColor || 'text.primary',
                  '&::placeholder': {
                    color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  }
                }
              }}
            />
          </Box>
        );

      case 'long-answer':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={getOptionValue('placeholder', 'Your detailed response')}
              value={value || getOptionValue('defaultValue', '')}
              onChange={(e) => handleAnswer(valueKey, e.target.value)}
              size="medium"
              required={Boolean(question.required)}
              error={hasError}
              helperText={hasError ? (getOptionValue('errorMessage') || errorMessage) : ''}
              key={valueKey}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  color: survey?.theme?.textColor || 'text.primary',
                  '& fieldset': {
                    borderColor: survey?.theme?.borderColor || 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: survey?.theme?.accentColor || '#1976d2'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: `${survey?.theme?.accentColor} !important`
                  },
                  '&.Mui-focused': {
                    bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  }
                },
                '& .MuiInputBase-input': {
                  color: survey?.theme?.textColor || 'text.primary',
                  '&::placeholder': {
                    color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  }
                }
              }}
            />
          </Box>
        );

      case 'dropdown':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl fullWidth>
              <Select
                value={value || getOptionValue('defaultValue', '')}
                onChange={(e) => handleAnswerForThis(e.target.value)}
                displayEmpty
                size="medium"
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
              >
                <MenuItem value="" disabled>
                  {getOptionValue('placeholder', 'Select an option')}
                </MenuItem>
                {(question.choices || question.options || []).map((option, idx) => (
                  <MenuItem key={idx} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {hasError && (
                <FormHelperText error={hasError}>
                  {getOptionValue('errorMessage') || errorMessage}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        );


      case 'email':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )
            }
            <TextField
              fullWidth
              type="email"
              placeholder={getOptionValue('placeholder', 'email@example.com')}
              value={value || getOptionValue('defaultValue', '')}
              onChange={(e) => handleAnswerForThis(e.target.value)}
              size="medium"
              required={Boolean(question.required)}
              error={hasError}
              helperText={hasError ? (getOptionValue('errorMessage') || errorMessage) : ''}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  color: survey?.theme?.textColor || 'text.primary',
                  '& fieldset': {
                    borderColor: survey?.theme?.borderColor || 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: survey?.theme?.accentColor || '#1976d2'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: `${survey?.theme?.accentColor} !important`
                  },
                  '&.Mui-focused': {
                    bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  }
                },
                '& .MuiInputBase-input': {
                  color: survey?.theme?.textColor || 'text.primary',
                  '&::placeholder': {
                    color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  }
                }
              }}
            />
          </Box>
        );

      case 'number':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl fullWidth variant="outlined">
              <OutlinedInput
                type="number"
                placeholder={getOptionValue('placeholder', '0')}
                value={value || getOptionValue('defaultValue', '')}
                onChange={(e) => handleAnswerForThis(e.target.value)}
                required={Boolean(question.required)}
                startAdornment={
                  <InputAdornment position="start">
                    <NumbersIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                }
                sx={{
                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                    '-webkit-appearance': 'none',
                    margin: 0,
                  },
                  '& input[type=number]': {
                    '-moz-appearance': 'textfield',
                  },
                  bgcolor: 'background.paper',
                }}
              />
              <FormHelperText error={hasError}>{hasError ? (getOptionValue('errorMessage') || errorMessage) : ''}</FormHelperText>
            </FormControl>
          </Box>
        );

      case 'date':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <TextField
              fullWidth
              type="date"
              value={value || getOptionValue('defaultValue', '')}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              size="medium"
              required={Boolean(question.required)}
              error={hasError}
              helperText={hasError ? (getOptionValue('errorMessage') || errorMessage) : ''}
              placeholder={question.config?.dateFormat || 'YYYY-MM-DD'}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: '9999-12-31',
                min: '1900-01-01'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  color: survey?.theme?.textColor || 'text.primary',
                  '& fieldset': {
                    borderColor: survey?.theme?.borderColor || 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: survey?.theme?.accentColor || '#1976d2'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: `${survey?.theme?.accentColor} !important`
                  },
                  '&.Mui-focused': {
                    bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  }
                },
                '& .MuiInputBase-input': {
                  color: survey?.theme?.textColor || 'text.primary',
                  '&::placeholder': {
                    color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  }
                }
              }}
            />
          </Box>
        );

      case 'phone':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl fullWidth variant="outlined">
              <OutlinedInput
                type="tel"
                placeholder={getOptionValue('placeholder', '+1 (123) 456-7890')}
                value={value || getOptionValue('defaultValue', '')}
                onChange={(e) => {
                  let formatted = e.target.value.replace(/\D/g, '');
                  if (formatted.length > 0) {
                    formatted = formatted.match(/(\d{0,1})(\d{0,3})(\d{0,3})(\d{0,4})/);
                    formatted = (!formatted[2] ? formatted[1] :
                      '+' + formatted[1] + ' (' + formatted[2] + ') ' +
                      (formatted[3] ? formatted[3] : '') +
                      (formatted[4] ? '-' + formatted[4] : ''));
                  }
                  handleAnswerForThis(formatted);
                }}
                required={Boolean(question.required)}
                startAdornment={
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                }
                sx={{
                  bgcolor: 'background.paper',
                  '& input': {
                    letterSpacing: '0.5px',
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                  }
                }}
              />
              <FormHelperText error={hasError}>{hasError ? (getOptionValue('errorMessage') || errorMessage) : ''}</FormHelperText>
            </FormControl>
          </Box>
        );


      case 'file-upload':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <input
              type="file"
              id={`file-${question.id}`}
              style={{ display: 'none' }
              }
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  let isValid = true;

                  // Check file size
                  if (question.maxFileSize && file.size > question.maxFileSize * 1024 * 1024) {
                    isValid = false;
                  }

                  // Check file type
                  if (isValid && question.allowedFileTypes?.length > 0) {
                    const fileExt = file.name.split('.').pop().toLowerCase();
                    if (!question.allowedFileTypes.includes(fileExt)) {
                      isValid = false;
                    }
                  }

                  if (!isValid) {
                    setValidationErrors(prev => ({
                      ...prev,
                      [question.id]: question.errorMessage
                    }));
                    e.target.value = ''; // Clear the file input
                    return;
                  }

                  setValidationErrors(prev => ({
                    ...prev,
                    [question.id]: null
                  }));
                  setAnswers({ ...answers, [question.id]: file });
                }
              }}
              accept={question.allowedFileTypes?.map(type => `.${type}`).join(',')}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <label htmlFor={`file-${question.id}`}>
                <Button variant="outlined" component="span">
                  Choose File
                </Button>
              </label>
              <Typography
                variant="caption"
                sx={{
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                Maximum file size: 5MB
              </Typography>
            </Box>
            {answers[question.id] && (
              <Typography 
                variant="caption" 
                sx={{ 
                  ml: 2, 
                  mt: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.primary'
                }}
              >
                {answers[question.id].name}
              </Typography>
            )}
            {validationErrors[question.id] && (
              <Typography 
                color="error" 
                variant="caption" 
                display="block" 
                sx={{ 
                  mt: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'error.main'
                }}
              >
                {getOptionValue('errorMessage') || validationErrors[question.id]}
              </Typography>
            )}
          </Box>
        );

      case 'rating':
        return (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {getOptionValue('caption') && (
              <Typography
                variant="body2"
                sx={{ 
                  mb: 1, 
                  textAlign: 'center', 
                  alignSelf: 'flex-start',
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <Rating
              value={Number(value) || 0}
              onChange={(e, newValue) => handleAnswerForThis(newValue)}
              size="large"
              max={question.config?.max || 5}
              emptyIcon={<StarBorderIcon sx={{ fontSize: '1.6rem', color: survey?.theme?.textColor || 'rgba(0,0,0,0.38)' }} />}
              icon={<StarIcon sx={{ fontSize: '1.6rem', color: survey?.theme?.textColor || '#FFD700' }} />}
              sx={{
                '& .MuiRating-iconEmpty': {
                  color: survey?.theme?.textColor || 'rgba(0,0,0,0.38)'
                },
                '& .MuiSvgIcon-root': {
                  color: survey?.theme?.textColor || '#FFD700'
                }
              }}
            />
            {question.config?.showLabels && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography 
                  variant="caption" 
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'text.secondary'
                  }}
                >
                  {question.config.labels?.[1] || 'Poor'}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'text.secondary'
                  }}
                >
                  {question.config.labels?.[question.config.max || 5] || 'Excellent'}
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 'multiple-choice':
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl required={Boolean(question.required)} sx={{ width: '100%' }}>
              <RadioGroup
                value={value || ''}
                onChange={(e) => handleAnswerForThis(e.target.value)}
              >
                {(question.choices || question.options || []).map((option, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      border: '2px solid',
                      borderColor: value === option
                        ? survey?.theme?.accentColor || '#1976d2'
                        : 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      cursor: 'grab',
                      mb: 1,
                      '&:hover': {
                        borderColor: survey?.theme?.accentColor || '#1976d2',
                        bgcolor: survey?.theme?.accentColor ? `${survey.theme.accentColor}05` : 'rgba(25, 118, 210, 0.02)'
                      }
                    }}
                    onClick={() => handleAnswerForThis(option)}
                  >
                    <FormControlLabel
                      value={option}
                      control={<Radio />}
                      label={
                        <Typography 
                          sx={{
                            color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                              ? '#ffffff' 
                              : 'text.primary',
                            fontSize: '1rem',
                            fontWeight: value === option ? 600 : 400,
                            py: 2,
                            pr: 2,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {option}
                        </Typography>
                      }
                      sx={{
                        margin: 0,
                        width: '100%',
                        alignItems: 'center',
                        '& .MuiFormControlLabel-label': {
                          flex: 1
                        }
                      }}
                    />
                  </Box>
                ))}
              </RadioGroup>
              {hasError && (
                <FormHelperText 
                  error={hasError}
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'error.main'
                  }}
                >
                  {getOptionValue('errorMessage') || errorMessage}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        );

      case 'checkboxes':
        const checkboxValues = value ? JSON.parse(value) : [];
        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl
              required={Boolean(question.required)}
              error={hasError}
              sx={{ width: '100%' }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(question.choices || question.options || []).map((option, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      border: '2px solid',
                      borderColor: checkboxValues.includes(option)
                        ? survey?.theme?.accentColor || '#1976d2'
                        : 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: survey?.theme?.accentColor || '#1976d2',
                        bgcolor: survey?.theme?.accentColor ? `${survey.theme.accentColor}05` : 'rgba(25, 118, 210, 0.02)'
                      }
                    }}
                    onClick={() => {
                      const newValues = checkboxValues.includes(option)
                        ? checkboxValues.filter(v => v !== option)
                        : [...checkboxValues, option];
                      handleAnswerForThis(JSON.stringify(newValues));
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxValues.includes(option)}
                          onChange={(e) => {
                            const newValues = e.target.checked
                              ? [...checkboxValues, option]
                              : checkboxValues.filter(v => v !== option);
                            handleAnswerForThis(JSON.stringify(newValues));
                          }}
                          sx={{
                            mx: 2,
                            my: 1.5,
                            color: survey?.theme?.borderColor || 'divider',
                            '&.Mui-checked': {
                              color: survey?.theme?.accentColor || '#1976d2'
                            }
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color: survey?.theme?.textColor || 'text.primary',
                            fontSize: '1rem',
                            fontWeight: checkboxValues.includes(option) ? 600 : 400,
                            py: 2,
                            pr: 2,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {option}
                        </Typography>
                      }
                      sx={{
                        margin: 0,
                        width: '100%',
                        alignItems: 'center',
                        '& .MuiFormControlLabel-label': {
                          flex: 1
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
              {hasError && (
                <FormHelperText 
                  error={hasError}
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'error.main'
                  }}
                >
                  {getOptionValue('errorMessage') || errorMessage}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        );

      case 'image-choice':
        // Parse the selected values properly
        let imageSelectedValues = [];
        if (value) {
          if (typeof value === 'string') {
            try {
              imageSelectedValues = JSON.parse(value);
              if (!Array.isArray(imageSelectedValues)) {
                imageSelectedValues = [value];
              }
            } catch (e) {
              imageSelectedValues = [value];
            }
          } else if (Array.isArray(value)) {
            imageSelectedValues = value;
          } else {
            imageSelectedValues = [value];
          }
        }

        return (
          <Box sx={{}}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl required={Boolean(question.required)} sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                {question.options?.map((option, idx) => {
                  const optionValue = typeof option === 'string' ? option : JSON.stringify(option);
                  const isSelected = question.allowMultipleSelection
                    ? imageSelectedValues.includes(optionValue)
                    : value === optionValue;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Box
                        sx={{
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          bgcolor: isSelected ? 'primary.light' : 'background.paper',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: isSelected ? 'primary.light' : 'primary.lighter'
                          }
                        }}
                        onClick={(e) => {
                          // Don't handle container clicks - let the FormControlLabel handle it
                          return;
                        }}
                      >
                        {/* Image Container */}
                        <Box sx={{ position: 'relative', aspectRatio: '16/10', bgcolor: 'grey.100' }}>
                          {(typeof option === 'object' && option.imageUrl) ? (
                            <img
                              src={option.imageUrl}
                              alt={option.label || `Option ${idx + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                console.warn('Failed to load image:', option.imageUrl);
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <Box sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.secondary'
                            }}>
                              <ImageIcon sx={{ fontSize: '3rem' }} />
                            </Box>
                          )}

                          {/* Selection Overlay */}
                          {isSelected && (
                            <Box sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white'
                            }}>
                              ✓
                            </Box>
                          )}
                        </Box>

                        {/* Content */}
                        <Box sx={{ p: 2 }}>
                          {question.allowMultipleSelection ? (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const newValues = e.target.checked
                                      ? [...imageSelectedValues, optionValue]
                                      : imageSelectedValues.filter(val => val !== optionValue);
                                    handleAnswerForThis(JSON.stringify(newValues));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                                      ? '#ffffff' 
                                      : 'text.primary'
                                  }}
                                >
                                  {(typeof option === 'object' ? option.label : option) || `Option ${idx + 1}`}
                                </Typography>
                              }
                              sx={{ margin: 0, width: '100%' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const newValues = isSelected
                                  ? imageSelectedValues.filter(val => val !== optionValue)
                                  : [...imageSelectedValues, optionValue];
                                handleAnswerForThis(JSON.stringify(newValues));
                              }}
                            />
                          ) : (
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={isSelected}
                                  value={optionValue}
                                  name={`image-choice-${question.id}`}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleAnswerForThis(optionValue);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                                      ? '#ffffff' 
                                      : 'text.primary'
                                  }}
                                >
                                  {(typeof option === 'object' ? option.label : option) || `Option ${idx + 1}`}
                                </Typography>
                              }
                              sx={{ margin: 0, width: '100%' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnswerForThis(optionValue);
                              }}
                            />
                          )}

                          {/* Description */}
                          {typeof option === 'object' && option.showDescription && option.description && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mt: 1,
                                color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                                  ? '#ffffff' 
                                  : 'text.secondary'
                              }}
                            >
                              {option.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              {hasError && (
                <FormHelperText 
                  error={hasError}
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'error.main'
                  }}
                >
                  {getOptionValue('errorMessage') || errorMessage}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        );

      case 'matrix':
        return (
          <Box sx={{ width: '100%' }}>
            {getOptionValue('caption') && (
              <Typography
                variant="body2"
                sx={{ 
                  mb: 1, 
                  display: 'block',
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <div className="matrix-preview">
              <div className="matrix-table-container" style={{ overflowX: 'auto', position: 'relative' }}>
                <table className="matrix-table" style={{ borderCollapse: 'separate' }}>
                  <colgroup>
                    <col style={{ position: 'sticky', left: 0, zIndex: 2, minWidth: '200px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th 
                        className="matrix-sticky-cell matrix-cell" 
                        style={{ 
                          position: 'sticky', 
                          left: 0, 
                          zIndex: 3,
                          color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                            ? '#ffffff' 
                            : 'text.primary'
                        }}
                      >
                        {/* Empty header cell */}
                      </th>
                      {question.columns?.map((col, idx) => (
                        <th 
                          key={idx} 
                          className="matrix-cell matrix-header-cell" 
                          align="center"
                          style={{
                            color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                              ? '#ffffff' 
                              : 'text.primary'
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {question.rows?.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td 
                          className="matrix-sticky-cell matrix-cell" 
                          style={{ 
                            position: 'sticky', 
                            left: 0, 
                            zIndex: 2,
                            color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                              ? '#ffffff' 
                              : 'text.primary'
                          }}
                        >
                          {row}
                        </td>
                        {question.columns?.map((_, colIdx) => (
                          <td key={colIdx} className="matrix-cell" align="center">
                            <div className="matrix-input-wrapper">
                              {question.allowMultipleSelection ? (
                                <Checkbox
                                  checked={Array.isArray(answers[question.id]?.[rowIdx]) &&
                                    answers[question.id]?.[rowIdx]?.includes(colIdx)}
                                  onChange={() => handleMatrixAnswer(question.id, rowIdx, colIdx)}
                                  sx={{
                                    '&.Mui-checked': {
                                      color: 'primary.main'
                                    }
                                  }}
                                />
                              ) : (
                                <Radio
                                  checked={answers[question.id]?.[rowIdx] === colIdx}
                                  onChange={() => handleMatrixAnswer(question.id, rowIdx, colIdx)}
                                  sx={{
                                    '&.Mui-checked': {
                                      color: 'primary.main'
                                    }
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Box>
        );

      case 'likert-scale':
        return (
          <Box sx={{ width: '100%' }}>
            {getOptionValue('caption') && (
              <Typography
                variant="body2"
                sx={{ 
                  mb: 1, 
                  display: 'block',
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell 
                    width="30%"
                    sx={{
                      color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                        ? '#ffffff' 
                        : 'text.primary'
                    }}
                  >
                  </TableCell>
                  {questionTypes['likert-scale'].config.scale.map((option, idx) => (
                    <TableCell key={idx} align="center" sx={{ px: 1 }}>
                      <Typography 
                        variant="body2"
                        sx={{
                          color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                            ? '#ffffff' 
                            : 'text.primary'
                        }}
                      >
                        {option}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'text.primary'
                  }}>
                    {question.title}
                  </TableCell>
                  {questionTypes['likert-scale'].config.scale.map((_, idx) => (
                    <TableCell key={idx} align="center" sx={{ cursor: 'pointer' }}>
                      <Radio
                        checked={Number(value) === idx}
                        onChange={() => handleAnswerForThis(idx)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          '&.Mui-checked': {
                            color: 'primary.main'
                          }
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        );

      case 'nps':
        const npsMin = question.config?.min || 0;
        const npsMax = question.config?.max || 10;
        const npsRange = Array.from({ length: npsMax - npsMin + 1 }, (_, i) => i + npsMin);

        // Calculate threshold points for color changes based on the range
        const rangeSize = npsMax - npsMin;
        const detractorThreshold = npsMin + Math.floor(rangeSize * 0.6); // First 60% for detractors
        const passiveThreshold = npsMin + Math.floor(rangeSize * 0.8); // Next 20% for passives
        // Remaining 20% for promoters

        return (
          <Box sx={{ width: '100%' }}>
            {getOptionValue('caption') && (
              <Typography
                variant="body2"
                sx={{ 
                  mb: 1, 
                  display: 'block',
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${npsRange.length}, 1fr)`,
              gap: { xs: 0.5, sm: 1 },
              mb: 2
            }}>
              {npsRange.map((num) => (
                <Box
                  key={num}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Button
                    variant={value === num ? 'contained' : 'outlined'}
                    onClick={() => handleAnswerForThis(num)}
                    sx={{
                      minWidth: { xs: '32px', sm: '48px' },
                      height: { xs: '32px', sm: '48px' },
                      p: 0,
                      borderRadius: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: 600,
                      borderColor: theme => {
                        if (num <= detractorThreshold) return 'error.light';
                        if (num <= passiveThreshold) return 'warning.light';
                        return 'success.light';
                      },
                      color: theme => {
                        if (!value && value !== 0) return 'text.primary';
                        if (value === num) return 'white';
                        if (num <= detractorThreshold) return 'error.main';
                        if (num <= passiveThreshold) return 'warning.main';
                        return 'success.main';
                      },
                      bgcolor: theme => {
                        if (value === num) {
                          if (num <= detractorThreshold) return 'error.main';
                          if (num <= passiveThreshold) return 'warning.main';
                          return 'success.main';
                        }
                        return 'transparent';
                      },
                      '&:hover': {
                        bgcolor: theme => {
                          if (value === num) {
                            if (num <= detractorThreshold) return 'error.dark';
                            if (num <= passiveThreshold) return 'warning.dark';
                            return 'success.dark';
                          }
                          if (num <= detractorThreshold) return 'error.lighter';
                          if (num <= passiveThreshold) return 'warning.lighter';
                          return 'success.lighter';
                        }
                      }
                    }}
                  >
                    {num}
                  </Button>
                </Box>
              ))}
            </Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 1,
              mt: 1
            }}>
              <Typography
                variant="caption"
                sx={{
                  position: 'relative',
                  pt: 2,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 1,
                    height: 8,
                    bgcolor: 'divider'
                  }
                }}
              >
                {question.config?.labels?.start || 'Not likely at all'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  position: 'relative',
                  pt: 2,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 1,
                    height: 8,
                    bgcolor: 'divider'
                  }
                }}
              >
                {question.config?.labels?.end || 'Extremely likely'}
              </Typography>
            </Box>
          </Box>
        );

      case 'ranking':
        // Use fallback options if ranking items not in state
        const currentRankingItems = rankingItems[valueKey] || [];
        
        console.log('Rendering ranking question:', question);
        console.log('Current ranking items from state:', currentRankingItems);
        
        // If no items in state, try to get options directly from question
        const displayItems = currentRankingItems.length > 0
          ? currentRankingItems
          : getRankingItems(question);
        
        console.log('Display items:', displayItems);
        
        return (
          <Box sx={{ width: '100%' }}>
            {getOptionValue('caption') && (
              <Typography
                variant="body2"
                sx={{ 
                  mb: 1, 
                  display: 'block',
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <div
              className="ranking-preview"
              style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
            >
              {displayItems.length > 0 ? (
                displayItems.map((item, index) => (
                  <Box
                    key={item.id}
                    className="ranking-item"
                    draggable
                    onDragStart={(e) => handleRankingDragStart(e, valueKey, item)}
                    onDragEnd={handleRankingDragEnd}
                    onDragOver={handleRankingDragOver}
                    onDrop={(e) => handleRankingDrop(e, valueKey, item)}
                    sx={{
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      '& .MuiTypography-root': {
                        minWidth: 0,
                        overflowWrap: 'anywhere'
                      },
                      '&:active': { cursor: 'grabbing' },
                      bgcolor: survey?.theme?.bg || 'background.paper',
                      '&:hover': {
                        borderColor: survey?.theme?.accentColor || '#1976d2',
                        // bgcolor: survey?.theme?.accentColor ? `${survey.theme.accentColor}05` : 'rgba(25, 118, 210, 0.02)'
                      }
                    }}
                  >
                    <span 
                      className="ranking-number"
                      style={{
                        color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                          ? '#ffffff' 
                          : 'text.primary',
                        padding: '16px',
                        fontWeight: 600
                      }}
                    >
                      {index + 1}
                    </span>
                    <DragIndicator 
                      sx={{ 
                        color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                          ? '#ffffff' 
                          : 'text.secondary', 
                        fontSize: 20,
                        mx: 1
                      }} 
                    />
                    <Typography
                      sx={{
                        color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                          ? '#ffffff' 
                          : 'text.primary',
                        fontSize: '1rem',
                        fontWeight: 400,
                        py: 2,
                        pr: 2,
                        flex: 1
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'text.secondary',
                    fontStyle: 'italic'
                  }}
                >
                  No options available
                </Typography>
              )}
            </div>
          </Box>
        );

      case 'datetime':
        return (
          <Box sx={{}}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <Typography 
                  variant="subtitle2" 
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'text.secondary',
                    gutterBottom: true
                  }}
                >
                  {question.config?.labels?.date || 'Date'}
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={value?.date || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const formattedDate = formatDateToUserFormat(date, question.config?.dateFormat);
                    handleAnswerForThis({
                      ...value,
                      date: date,
                      displayDate: formattedDate
                    });
                  }}
                  variant="outlined"
                  required={Boolean(question.required)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: `${survey?.theme?.inputBg || 'transparent'} !important`,
                      color: survey?.theme?.textColor || 'text.primary',
                      '& fieldset': {
                        borderColor: survey?.theme?.borderColor || 'divider'
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(survey?.theme?.accentColor || '#1976d2', 0.7)
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: survey?.theme?.accentColor || '#1976d2',
                        borderWidth: '2px'
                      },
                      '&.Mui-focused': {
                        bgcolor: `${survey?.theme?.inputBg || 'transparent'} !important`
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: survey?.theme?.textColor || 'text.primary'
                    }
                  }}
                  InputLabelProps={{ shrink: false }}
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography 
                  variant="subtitle2" 
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'text.secondary',
                    gutterBottom: true
                  }}
                >
                  {question.config?.labels?.time || 'Time'}
                </Typography>
                <TextField
                  type="time"
                  fullWidth
                  value={value?.time || ''}
                  onChange={(e) => {
                    handleAnswerForThis({
                      ...value,
                      time: e.target.value,
                    });
                  }}
                  placeholder="HH:MM"
                  variant="outlined"
                  required={Boolean(question.required)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: `${survey?.theme?.inputBg || 'transparent'} !important`,
                      color: survey?.theme?.textColor || 'text.primary',
                      '& fieldset': {
                        borderColor: survey?.theme?.borderColor || 'divider'
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(survey?.theme?.accentColor || '#1976d2', 0.7)
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: survey?.theme?.accentColor || '#1976d2',
                        borderWidth: '2px'
                      },
                      '&.Mui-focused': {
                        bgcolor: `${survey?.theme?.inputBg || 'transparent'} !important`
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: survey?.theme?.textColor || 'text.primary'
                    }
                  }}
                />
              </FormControl>
            </Box>
            {(hasError || getOptionValue('caption')) && (
              <FormHelperText
                error={hasError}
                sx={{
                  mt: 1,
                  color: hasError ? 'error.main' : 'text.secondary'
                }}
              >
                {hasError && (getOptionValue('errorMessage') || errorMessage)}
              </FormHelperText>
            )}
          </Box>
        );

      case 'time':
        const formatTime = (timeStr) => {
          if (!timeStr) return '';
          const [hours, minutes] = timeStr.split(':');
          const hour = parseInt(hours);

          if (question.config?.timeFormat === '12') {
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour % 12 || 12;
            return `${String(displayHour).padStart(2, '0')}:${minutes} ${period}`;
          }
          return `${hours}:${minutes}`;
        };

        return (
          <Box sx={{}}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <Typography 
                  variant="subtitle2" 
                  sx={{
                    color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                      ? '#ffffff' 
                      : 'text.secondary',
                    gutterBottom: true
                  }}
                >
                  {question.config?.labels?.time || 'Time'}
                </Typography>
                <OutlinedInput
                  type="text"
                  fullWidth
                  value={value?.displayTime || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    handleAnswerForThis({
                      ...value,
                      displayTime: inputValue,
                      rawTime: inputValue
                    });
                  }}
                  onFocus={(e) => {
                    e.target.type = 'time';
                    e.target.value = value?.rawTime || '';
                  }}
                  onBlur={(e) => {
                    e.target.type = 'text';
                    if (e.target.value) {
                      handleAnswer(question.id, {
                        ...value,
                        rawTime: e.target.value,
                        displayTime: formatTime(e.target.value)
                      });
                    }
                  }}
                  placeholder="Select time"
                  required={Boolean(question.required)}
                  sx={{
                    '& input': {
                      padding: '12px',
                      cursor: 'pointer',
                      color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                        ? '#ffffff' 
                        : 'text.primary'
                    }
                  }}
                />
              </FormControl>
            </Box>
            {(hasError) && (
              <FormHelperText 
                error={hasError} 
                sx={{ 
                  mt: 1,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'error.main'
                }}
              >
                {hasError && (getOptionValue('errorMessage') || errorMessage)}
              </FormHelperText>
            )}
          </Box>
        );

      case 'website':
        const validateUrl = (url) => {
          if (!url) return true;
          // Basic URL validation regex
          const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- .\/?%&=]*)?$/;
          return urlPattern.test(url);
        };

        return (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  alignSelf: 'flex-start',
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl fullWidth variant="outlined">
              <OutlinedInput
                type="url"
                placeholder={getOptionValue('placeholder', 'https://example.com')}
                value={value || getOptionValue('defaultValue', '')}
                onChange={(e) => {
                  const url = e.target.value;
                  const isValid = validateUrl(url);
                  handleAnswerForThis(url);
                  // Show error if URL is invalid and user has typed something
                  if (url && !isValid) {
                    setValidationErrors(prev => ({
                      ...prev,
                      [valueKey]: getOptionValue('errorMessage', 'Please enter a valid URL')
                    }));
                  } else {
                    setValidationErrors(prev => ({
                      ...prev,
                      [valueKey]: null
                    }));
                  }
                }}
                required={Boolean(question.required)}
                error={hasError}
                startAdornment={
                  <InputAdornment position="start">
                    <WebsiteIcon 
                      sx={{ 
                        color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                          ? '#ffffff' 
                          : 'text.secondary' 
                      }} 
                    />
                  </InputAdornment>
                }
                sx={{
                  bgcolor: 'background.paper',
                }}
              />
              <FormHelperText 
                error={hasError}
                sx={{
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'error.main'
                }}
              >
                {hasError ? (getOptionValue('errorMessage') || errorMessage) : ''}
              </FormHelperText>
            </FormControl>
          </Box>
        );

      case 'address':
        const addressValue = value ? JSON.parse(value) : {};

        return (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {getOptionValue('caption') && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  display: 'block', 
                  alignSelf: 'flex-start',
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  mb: 0.5,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                Street Address {Boolean(question.required) && '*'}
              </Typography>
              <OutlinedInput
                fullWidth
                placeholder="Enter your street address"
                value={addressValue.street || ''}
                onChange={(e) => {
                  const newValue = {
                    ...addressValue,
                    street: e.target.value
                  };
                  handleAnswerForThis(JSON.stringify(newValue));
                }}
                required={Boolean(question.required)}
                // sx={{
                //   bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                //   color: survey?.theme?.textColor || 'text.primary',
                //   '& fieldset': {
                //     borderColor: survey?.theme?.borderColor || 'divider'
                //   },
                //   '&:hover fieldset': {
                //     borderColor: survey?.theme?.accentColor || '#1976d2'
                //   },
                //   '&.Mui-focused fieldset': {
                //     borderColor: `${survey?.theme?.accentColor} !important`
                //   },
                //   '&.Mui-focused': {
                //     bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                //   },
                //   '& input': {
                //     color: survey?.theme?.textColor || 'text.primary',
                //     '&::placeholder': {
                //       color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                //     }
                //   }
                // }}
//              
sx={{
  color: survey?.theme?.textColor || 'text.primary',

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& input': {
    color: survey?.theme?.textColor || 'text.primary',
    '&::placeholder': {
      color: survey?.theme?.textColor
        ? `${survey.theme.textColor}80`
        : 'text.secondary',
    },
  },
}}


              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <Typography 
                variant="caption" 
                sx={{ 
                  mb: 0.5,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                  City {Boolean(question.required) && '*'}
                </Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Enter city"
                  value={addressValue.city || ''}
                  onChange={(e) => {
                    const newValue = {
                      ...addressValue,
                      city: e.target.value
                    };
                    handleAnswerForThis(JSON.stringify(newValue));
                  }}
                  required={Boolean(question.required)}
                  // sx={{
                  //   bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  //   color: survey?.theme?.textColor || 'text.primary',
                  //   '& fieldset': {
                  //     borderColor: survey?.theme?.borderColor || 'divider'
                  //   },
                  //   '&:hover fieldset': {
                  //     borderColor: survey?.theme?.accentColor || '#1976d2'
                  //   },
                  //   '&.Mui-focused fieldset': {
                  //     borderColor: `${survey?.theme?.accentColor} !important`
                  //   },
                  //   '&.Mui-focused': {
                  //     bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  //   },
                  //   '& input': {
                  //     color: survey?.theme?.textColor || 'text.primary',
                  //     '&::placeholder': {
                  //       color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  //     }
                  //   }
                  // }}
                  sx={{
  color: survey?.theme?.textColor || 'text.primary',

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& input': {
    color: survey?.theme?.textColor || 'text.primary',
    '&::placeholder': {
      color: survey?.theme?.textColor
        ? `${survey.theme.textColor}80`
        : 'text.secondary',
    },
  },
}}
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography 
                variant="caption" 
                sx={{ 
                  mb: 0.5,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                  State/Province {Boolean(question.required) && '*'}
                </Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Enter state/province"
                  value={addressValue.state || ''}
                  onChange={(e) => {
                    const newValue = {
                      ...addressValue,
                      state: e.target.value
                    };
                    handleAnswerForThis(JSON.stringify(newValue));
                  }}
                  required={Boolean(question.required)}
                  // sx={{
                  //   bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  //   color: survey?.theme?.textColor || 'text.primary',
                  //   '& fieldset': {
                  //     borderColor: survey?.theme?.borderColor || 'divider'
                  //   },
                  //   '&:hover fieldset': {
                  //     borderColor: survey?.theme?.accentColor || '#1976d2'
                  //   },
                  //   '&.Mui-focused fieldset': {
                  //     borderColor: `${survey?.theme?.accentColor} !important`
                  //   },
                  //   '&.Mui-focused': {
                  //     bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  //   },
                  //   '& input': {
                  //     color: survey?.theme?.textColor || 'text.primary',
                  //     '&::placeholder': {
                  //       color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  //     }
                  //   }
                  // }}
                  sx={{
  color: survey?.theme?.textColor || 'text.primary',

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& input': {
    color: survey?.theme?.textColor || 'text.primary',
    '&::placeholder': {
      color: survey?.theme?.textColor
        ? `${survey.theme.textColor}80`
        : 'text.secondary',
    },
  },
}}
                />
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <Typography 
                variant="caption" 
                sx={{ 
                  mb: 0.5,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                  ZIP/Postal Code {Boolean(question.required) && '*'}
                </Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Enter postal code"
                  value={addressValue.zip || ''}
                  onChange={(e) => {
                    const newValue = {
                      ...addressValue,
                      zip: e.target.value
                    };
                    handleAnswerForThis(JSON.stringify(newValue));
                  }}
                  required={Boolean(question.required)}
                  // sx={{
                  //   bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  //   color: survey?.theme?.textColor || 'text.primary',
                  //   '& fieldset': {
                  //     borderColor: survey?.theme?.borderColor || 'divider'
                  //   },
                  //   '&:hover fieldset': {
                  //     borderColor: survey?.theme?.accentColor || '#1976d2'
                  //   },
                  //   '&.Mui-focused fieldset': {
                  //     borderColor: `${survey?.theme?.accentColor} !important`
                  //   },
                  //   '&.Mui-focused': {
                  //     bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  //   },
                  //   '& input': {
                  //     color: survey?.theme?.textColor || 'text.primary',
                  //     '&::placeholder': {
                  //       color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  //     }
                  //   }
                  // }}
                  sx={{
  color: survey?.theme?.textColor || 'text.primary',

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& input': {
    color: survey?.theme?.textColor || 'text.primary',
    '&::placeholder': {
      color: survey?.theme?.textColor
        ? `${survey.theme.textColor}80`
        : 'text.secondary',
    },
  },
}}
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography 
                variant="caption" 
                sx={{ 
                  mb: 0.5,
                  color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                    ? '#ffffff' 
                    : 'text.secondary'
                }}
              >
                  Country {Boolean(question.required) && '*'}
                </Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Enter country"
                  value={addressValue.country || ''}
                  onChange={(e) => {
                    const newValue = {
                      ...addressValue,
                      country: e.target.value
                    };
                    handleAnswerForThis(JSON.stringify(newValue));
                  }}
                  required={Boolean(question.required)}
                  // sx={{
                  //   bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`,
                  //   color: survey?.theme?.textColor || 'text.primary',
                  //   '& fieldset': {
                  //     borderColor: survey?.theme?.borderColor || 'divider'
                  //   },
                  //   '&:hover fieldset': {
                  //     borderColor: survey?.theme?.accentColor || '#1976d2'
                  //   },
                  //   '&.Mui-focused fieldset': {
                  //     borderColor: `${survey?.theme?.accentColor} !important`
                  //   },
                  //   '&.Mui-focused': {
                  //     bgcolor: `${survey?.theme?.inputBg || 'background.paper'} !important`
                  //   },
                  //   '& input': {
                  //     color: survey?.theme?.textColor || 'text.primary',
                  //     '&::placeholder': {
                  //       color: survey?.theme?.textColor ? `${survey.theme.textColor}80` : 'text.secondary'
                  //     }
                  //   }

                  // }}
                  sx={{
  color: survey?.theme?.textColor || 'text.primary',

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& input': {
    color: survey?.theme?.textColor || 'text.primary',
    '&::placeholder': {
      color: survey?.theme?.textColor
        ? `${survey.theme.textColor}80`
        : 'text.secondary',
    },
  },
}}
                />
              </FormControl>
            </Box>
          </Box>
        );

      // Inside the renderQuestion function, add these cases:

      case 'range-scale':
        return (
          <Box sx={{
            width: '100%',
            px: { xs: 2, sm: 2 },
            pt: 1,
            pb: { xs: 8, sm: 4 },
            position: 'relative'
          }}>
            {getOptionValue('caption') && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, display: 'block', textAlign: 'left' }}
              >
                {getOptionValue('caption')}
              </Typography>
            )}
            <Box sx={{
              width: '100%',
              '& .MuiSlider-root': {
                width: '100%',
                mt: 3,
                mb: 3
              },
              '& .MuiSlider-markLabel': {
                transform: {
                  xs: 'translate(-50%, 24px) scale(0.75)',
                  sm: 'translate(-50%, 24px)'
                },
                whiteSpace: 'nowrap',
                width: 'auto',
                fontSize: {
                  xs: '11px',
                  sm: '0.875rem'
                },
                color: 'text.secondary',
                '&[data-index="0"]': {
                  transform: {
                    xs: 'translate(0%, 24px) scale(0.75)',
                    sm: 'translate(0%, 24px)'
                  }
                },
                '&[data-index="2"]': {
                  transform: {
                    xs: 'translate(-100%, 24px) scale(0.75)',
                    sm: 'translate(-100%, 24px)'
                  }
                }
              }
            }}>
              <Slider
                value={parseInt(value) || question.config?.defaultValue || 50}
                onChange={(_, newValue) => handleAnswerForThis(newValue)}
                min={question.config?.min || 0}
                max={question.config?.max || 100}
                step={question.config?.step || 1}
                marks={question.config?.showTicks && [
                  {
                    value: question.config?.min || 0,
                    label: question.config?.labels?.start || 'Low'
                  },
                  {
                    value: (question.config?.max + question.config?.min) / 2 || 50,
                    label: question.config?.labels?.middle || 'Medium'
                  },
                  {
                    value: question.config?.max || 100,
                    label: question.config?.labels?.end || 'High'
                  }
                ]}
              />
            </Box>
          </Box>
        );

      case 'image-choice':
        // Parse existing answers correctly - handle both string and array formats
        const selectedValues = (() => {
          if (!value) return [];

          if (question.allowMultipleSelection) {
            try {
              return typeof value === 'string' ? JSON.parse(value) : (Array.isArray(value) ? value : []);
            } catch (e) {
              return [];
            }
          } else {
            return [value];
          }
        })();

        return (
          <Box sx={{ width: '100%' }}>
            <Grid container spacing={2}>
              {(Array.isArray(question.options) ? question.options : []).map((option, idx) => {
                // Create a unique identifier for each option
                const optionValue = typeof option === 'string' ? option : (option.label || `Option ${idx + 1}`);
                const imageUrl = typeof option === 'object' ? option.imageUrl : null;
                const description = typeof option === 'object' ? option.description : null;
                const showDescription = typeof option === 'object' ? option.showDescription : false;
                const isSelected = selectedValues.includes(optionValue);

                return (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (question.allowMultipleSelection) {
                          const newValues = isSelected
                            ? selectedValues.filter(v => v !== optionValue)
                            : [...selectedValues, optionValue];
                          handleAnswerForThis(JSON.stringify(newValues));
                        } else {
                          handleAnswerForThis(optionValue);
                        }
                      }}
                      sx={{
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={optionValue}
                            style={{
                              width: '100%',
                              height: 'auto',
                              aspectRatio: '16/9',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              bgcolor: 'grey.100',
                              aspectRatio: '16/9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <FormControlLabel
                          control={
                            question.allowMultipleSelection ? (
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newValues = e.target.checked
                                    ? [...selectedValues, optionValue]
                                    : selectedValues.filter(v => v !== optionValue);
                                  handleAnswerForThis(JSON.stringify(newValues));
                                }}
                                name={`checkbox-${question.id}-${idx}`}
                              />
                            ) : (
                              <Radio
                                checked={value === optionValue}
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    handleAnswerForThis(optionValue);
                                  }
                                }}
                                name={`radio-group-${question.id}`}
                                value={optionValue}
                              />
                            )
                          }
                          label={optionValue}
                          sx={{
                            width: '100%',
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              width: '100%'
                            }
                          }}
                        />
                        {showDescription && description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            {(hasError) && (
              <FormHelperText error={hasError}>
                {hasError && (getOptionValue('errorMessage') || errorMessage)}
              </FormHelperText>
            )}
          </Box>
        );


      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '100vw',
            height: '100vh',
            maxWidth: 'none',
            maxHeight: 'none',
            margin: 0,
            borderRadius: 0,
            backgroundColor: `${customTheme.palette.background.default} !important`,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10
        }}>
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 600 }}
          >
            {survey?.name || 'Survey Preview'}
          </Typography>
          {/* <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton> */}
          <IconButton
            onClick={onClose}
            sx={{
              // use survey text color in dark themes (or provided theme textColor), fallback to inherited color for light themes
              color: survey?.theme?.textColor ? survey.theme.textColor : 'inherit',
              // slightly increase contrast when dark theme detected
              ...(survey?.theme && (survey.theme.id === 'dark-modern' || survey.theme.id === 'discord-dark') ? {
                bgcolor: 'transparent',
                '&:hover': { bgcolor: alpha(survey.theme.textColor || '#ffffff', 0.06) }
              } : {})
            }}
            aria-label="Close preview"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {/* Question tracking bar - Only show for non-classic layout */}
        {!isMobile && !isClassicLayout && (
          <Box sx={{
            position: 'fixed',
            top: 80,
            left: 0,
            right: 0,
            zIndex: 9,
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 0,
          }}>
            <Box
              sx={{
                width: '100%',
                position: 'relative',
                px: 2,
              }}
            >
              <Box
                sx={{
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  '-ms-overflow-style': 'none',
                  py: 2
                }}
              >
                <Stepper
                  activeStep={Math.max(0, currentVisiblePosition)}
                  sx={{
                    minWidth: 'max-content',
                    mx: 'auto',
                    px: 4,
                    '& .MuiStepLabel-label': {
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      '&.Mui-active': {
                        color: 'primary.main',
                        fontWeight: 600
                      }
                    },
                    '& .MuiStepIcon-root': {
                      color: 'divider',
                      '&.Mui-active': {
                        color: 'primary.main',
                      },
                      '&.Mui-completed': {
                        color: 'primary.light',
                      }
                    },
                    '& .MuiStep-root': {
                      px: 2,
                    }
                  }}
                >
                  {visibleQuestionIndexes.map((index) => (
                    <Step key={index}>
                      <StepLabel>{`Question ${visibleQuestionIndexes.indexOf(index) + 1}`}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>
          </Box>
        )}

        <DialogContent
          sx={dialogContentStyle}
        >
          {logicLoading && <Alert severity="info" sx={{ mb: 2 }}>Loading survey logic rules…</Alert>}
          {logicError && <Alert severity="error" sx={{ mb: 2 }}>Logic rules could not be loaded: {logicError}</Alert>}
          {logicEvaluation.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Survey logic needs attention: {logicEvaluation.errors.map(item => item.error).filter(Boolean).join('; ')}
            </Alert>
          )}
          {logicEvaluation.messagesToShow.map((item, index) => (
            <Alert key={`${item.message}-${index}`} severity={['error', 'warning', 'success', 'info'].includes(item.type) ? item.type : 'info'} sx={{ mb: 2 }}>
              {item.message}
            </Alert>
          ))}
          {logicLoading || logicError ? null : previewEnded ? (
            <Alert severity="info" sx={{ mt: 12, mx: 'auto', width: 'fit-content' }}>
              {logicEvaluation.messagesToShow[0]?.message || 'This survey would end here.'}
            </Alert>
          ) : isClassicLayout ? (
            // Classic layout - all questions on one page
            <Container
              maxWidth="md"
              sx={{
                width: '100%',
                py: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                mt: 10,
              }}
            >
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                {survey?.name || 'Survey Preview'}
              </Typography>
              <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                color: (survey?.theme?.id === 'dark-modern' || survey?.theme?.id === 'discord-dark') 
                  ? '#ffffff' 
                  : 'text.secondary'
              }}
            >
              Please answer the following questions
            </Typography>
              <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}>
                {visibleQuestionIndexes.map((index) => {
                  const question = questions[index];
                  return (
                  <Box
                    key={question.id || index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 600
                      }}
                    >
                      {question.title}
                      {Boolean(question.required) && (
                        <Typography component="span" color="error" sx={{ ml: 1 }}>*</Typography>
                      )}
                    </Typography>
                    {renderQuestion(question, index)}
                  </Box>
                  );
                })}
                {logicEvaluation.endSurvey && <Alert severity="info">This rule ends the survey after the current answer.</Alert>}
              </Box>
            </Container>

          ) : (
            <>
              {questions[activeStep] && questionVisibility[String(questions[activeStep].id)] !== false && (
                <>
                  <Box
                    sx={questionContainerStyle}
                  >
                    <Box sx={{ mb: 6, textAlign: 'center', width: '100%' }}>
                      <Typography
                        variant="overline"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 600,
                          letterSpacing: 1
                        }}
                      >
                        Question {currentVisiblePosition + 1} of {visibleQuestionIndexes.length}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          mt: 2,
                          color: 'text.primary',
                          fontWeight: 600,
                          lineHeight: 1.4
                        }}
                      >
                        {questions[activeStep].title}
                        {Boolean(questions[activeStep].required) && (
                          <Typography
                            component="span"
                            color="error"
                            sx={{ ml: 1 }}
                          >
                            *
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Box sx={{  }}>
                      {renderQuestion(questions[activeStep], activeStep)}
                      {/* <QuestionRenderer
                                question={questions[activeStep]}
                                value={answers[questions[activeStep].id] || ''}
                                onChange={(value) => handleAnswer(questions[activeStep].id, value)}
                                isPreview={true}
                                error={Boolean(validationErrors[questions[activeStep].id])}
                                helperText={
                                  validationErrors[questions[activeStep].id] || 
                                  questions[activeStep].caption
                                }
                            /> */}

                    </Box>
                  </Box>

                  <Box sx={navigationStyle}>
                    <Button
                      onClick={handleBack}
                      disabled={currentVisiblePosition <= 0}
                      startIcon={<PrevIcon />}
                      sx={{
                        visibility: activeStep === 0 ? 'hidden' : 'visible',
                        px: 4,
                        py: 2,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': {
                          bgcolor: 'background.paper',
                          boxShadow: 4
                        }
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={handleNext}
                      disabled={activeStep === lastVisibleQuestionIndex}
                      endIcon={<NextIcon />}
                      variant="contained"
                      sx={{
                        px: 4,
                        py: 2,
                        boxShadow: 2,
                        '&:hover': {
                          boxShadow: 4
                        }
                      }}
                    >
                      {activeStep === lastVisibleQuestionIndex ? 'Finish Preview' : 'Next'}
                    </Button>
                  </Box>
                </>
              )}

            </>

          )}



        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};


export default PreviewSurvey;
